"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Portfolio/PlaidTrialExpiredBanner/PlaidTrialExpiredBanner";

import React from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";

import { PLAID_FREE_TRIAL_DAYS_DEFAULT } from "@/src/hooks/plaid/plaidGateRemoteConfigShared";

const I18n = I18nUtil.createScoped(Translations);

export interface PlaidTrialExpiredBannerProps {
  className?: string;
  /** 文案中的免费天数（来自 RC；默认 30） */
  freeTrialDays?: number;
  /** 可选：试链到期时间（ms），本期最小提示可不展示 */
  trialExpiresAt?: number | null;
  onResumeSync?: () => void;
}

/**
 * Plaid 试链到期提醒卡（Figma AlertCard `101951:175019` + CTA `101951:175026`）。
 *
 * 文案待补 translate（目录只读）：
 * - "I've paused syncing your accounts"
 * - "Your {days} free days are up. Restore and I'll pick up right where we left off."
 * - "Resume account sync"
 */
export const PlaidTrialExpiredBanner: React.FC<
  PlaidTrialExpiredBannerProps
> = ({
  className,
  freeTrialDays = PLAID_FREE_TRIAL_DAYS_DEFAULT,
  onResumeSync,
}) => {
  const days =
    Number.isFinite(freeTrialDays) && freeTrialDays! > 0
      ? Math.floor(freeTrialDays!)
      : PLAID_FREE_TRIAL_DAYS_DEFAULT;

  return (
    <div
      role="status"
      data-testid="plaid-trial-expired-banner"
      data-openreplay-label="Portfolio · Plaid trial expired"
      className={cn("w-full", className)}
    >
      <div
        className={cn(
          "flex flex-col gap-3 overflow-hidden rounded-3 p-4",
          "bg-background_accent3_overlay10",
        )}
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-2",
                "bg-background_solid_neutral_primary",
              )}
              aria-hidden
            >
              <Icon
                name="postpone"
                size={20}
                className="text-content_accent3_primary size-5"
              />
            </div>
            <p className="title_l text-content_primary">
              {I18n.t("I've paused syncing your accounts")}
            </p>
          </div>
          <p className="body_s text-content_secondary">
            {`Your ${days} free days are up. Restore and I'll pick up right where we left off.`}
          </p>
        </div>
        <button
          type="button"
          data-testid="plaid-trial-expired-resume-cta"
          data-tracking-eventname="plaid_restore_cta_tap"
          data-openreplay-label="Portfolio · Resume account sync"
          className={cn(
            "inline-flex min-w-20 shrink-0 items-center justify-center gap-0.5 overflow-hidden rounded-2 px-3 py-2",
            "bg-background_accent4_overlay100 text-content_inverse_primary body_medium_m",
            "hover:brightness-95 active:brightness-90",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background_accent4_overlay100/60",
            "self-start",
          )}
          onClick={onResumeSync}
        >
          {I18n.t("Resume account sync")}
        </button>
      </div>
    </div>
  );
};
